import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import {
  appRunbookQuery,
  createRunbook,
  linkRunbook,
  runbookSolutions,
  saveRunbook,
  type AppRunbookPage,
  type Runbook,
  type RunbookSolution,
  type RunbookStep,
} from '../api/runbooks'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'
import { failed } from './parts'
import './AppRunbook.css'

/**
 * "Runbook / What to do when it fails" pe fisa aplicatiei -
 * `v4/api/src/BusinessApps/Runbook/Runbooks.php`.
 *
 * Runbook-ul e O SOLUTIE DIN IT SOLUTIONS (baza de cunostinte a tehnicienilor),
 * in categoria firmei "Application runbooks". Pasii sunt listele numerotate din
 * cele patru niveluri ale solutiei (Level 0 primul raspuns ... Level 3
 * furnizorul), numerotate o data, in ordinea nivelurilor.
 *
 *  - "Create runbook" face solutia din sablon si o leaga;
 *  - "Edit steps" scrie pasii IN solutie (textul din afara listei ramane);
 *  - "Link existing solution" leaga una din biblioteca firmei.
 *
 * Pe tichet, aceiasi pasi stau sus in fila IT Solutions
 * (`easeedesk/TicketSolutionRunbooks.tsx`), cu bife.
 *
 * Fara drept sau inainte de migrare, cardul nu apare.
 */
export const RUNBOOK_ANCHOR = 'app-sec-runbook'

/** Culorile nivelurilor, ca in IT Solutions (`Solutions::LEVEL_COLORS`). */
export const LEVEL_COLORS: Record<number, string> = { 0: '#d83430', 1: '#ffa500', 2: '#ffcc00', 3: '#359c45' }

const DEFAULT_LEVELS = [
  { value: 0, label: 'Level 0 - first response' },
  { value: 1, label: 'Level 1 - technician' },
  { value: 2, label: 'Level 2 - specialist' },
  { value: 3, label: 'Level 3 - vendor' },
]

export function AppRunbook({ appId }: { appId: number }) {
  const query = useQueryClient()
  const { data } = useQuery({ ...appRunbookQuery(appId), retry: false })
  const [editing, setEditing] = useState(false)
  const [linking, setLinking] = useState(false)
  const [busy, setBusy] = useState(false)

  if (!data || !data.ready) return null
  const rb = data.runbook ?? null
  const usable = rb !== null && !rb.missing && !rb.restricted
  const refresh = () => void query.invalidateQueries({ queryKey: ['business-apps'] })

  const create = () => {
    setBusy(true)
    createRunbook(appId)
      .then((r) => {
        toast(r.status === 'OK' ? `${r.message} Edit the steps to fit this application.` : r.message, 'Runbook', r.status === 'OK' ? 'success' : 'error')
        if (r.status === 'OK') refresh()
      })
      .catch((error) => toast(failed(error), 'Runbook', 'error'))
      .finally(() => setBusy(false))
  }

  const unlink = () => {
    setBusy(true)
    linkRunbook(appId, 0)
      .then((r) => {
        toast(r.message, 'Runbook', r.status === 'OK' ? 'success' : 'error')
        if (r.status === 'OK') refresh()
      })
      .catch((error) => toast(failed(error), 'Runbook', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <>
      <div className="adm-section" id={RUNBOOK_ANCHOR}>
        <h4>Runbook</h4>
        <span className="adm-section-note">what to do when it fails · kept in IT Solutions</span>
        <span className="rb-actions">
          {data.canEdit && usable && (
            <button type="button" id="runbookEdit" className="btn btn-default btn-xs" onClick={() => setEditing(true)}>
              <i className="fa fa-pencil-alt"></i> Edit steps
            </button>
          )}
          {data.canEdit && !usable && !rb?.restricted && (
            <button type="button" id="runbookCreate" className="btn btn-default btn-xs" disabled={busy} onClick={create}>
              <i className="fas fa-plus"></i> Create runbook
            </button>
          )}
          {data.canLink && (
            <button type="button" id="runbookLink" className="btn btn-default btn-xs" onClick={() => setLinking(true)}>
              <i className="fas fa-link"></i> {rb ? 'Link another solution' : 'Link existing solution'}
            </button>
          )}
        </span>
      </div>
      <div className="adm-card" id="appRunbook">
        {rb === null ? (
          <p className="text-muted rb-empty">
            No runbook yet. A runbook is a solution in Easeedesk &rsaquo; IT Solutions ({data.category ?? 'Application runbooks'}): the steps a
            technician follows when this application fails, by support level. Tickets on its servers show it on top of their IT Solutions tab.
          </p>
        ) : rb.missing ? (
          <p className="text-muted rb-empty" id="runbookMissing">
            The linked IT Solutions solution #{rb.solutionId} was removed or switched off. Create a new runbook or link another solution.
          </p>
        ) : rb.restricted ? (
          <p className="text-muted rb-empty" id="runbookRestricted">
            The runbook is the solution <strong>{rb.title}</strong>, in an IT Solutions category your role cannot read.
          </p>
        ) : (
          <>
            <p className="rb-source">
              <i className="fas fa-lightbulb" aria-hidden="true"></i> IT Solutions &rsaquo; {rb.category} &rsaquo;{' '}
              <a href={data.libraryUrl ?? '/app/easeedesk/it_solutions'} target="_blank" rel="noopener noreferrer">
                {rb.title}
              </a>
            </p>
            {rb.steps.length === 0 ? (
              <p className="text-muted rb-empty">This solution has no numbered steps yet.</p>
            ) : (
              <RunbookLevels runbook={rb} />
            )}
            <div className="rb-meta">
              <span className="adm-muted small" id="runbookLinked">
                Linked by {rb.linkedBy}
                {rb.linkedAt !== '' && <> · {rb.linkedAt}</>}
                {rb.convertedFrom !== null && <> · converted from the runbook version {rb.convertedFrom}</>}
              </span>
              {data.canLink && (
                <button type="button" className="btn btn-link btn-xs" id="runbookUnlink" disabled={busy} onClick={unlink}>
                  Unlink
                </button>
              )}
            </div>
          </>
        )}
      </div>
      {editing && rb && usable && <RunbookModal appId={appId} page={data} runbook={rb} onClose={() => setEditing(false)} />}
      {linking && <LinkModal appId={appId} onClose={() => setLinking(false)} />}
    </>
  )
}

/**
 * Pasii pe niveluri, numerotati o data. `tick` (pe tichet) pune cate o bifa
 * in fata fiecarui pas.
 */
export function RunbookLevels({ runbook, tick }: { runbook: Pick<Runbook, 'steps' | 'intros'>; tick?: (step: RunbookStep) => ReactNode }) {
  const levels = [0, 1, 2, 3].filter((l) => runbook.steps.some((s) => s.level === l) || runbook.intros.some((i) => i.level === l))
  return (
    <div className="rb-levels">
      {levels.map((level) => {
        const steps = runbook.steps.filter((s) => s.level === level)
        const intro = runbook.intros.find((i) => i.level === level)
        return (
          <section key={level} className="rb-level" data-level={level}>
            <div className="rb-level-name">
              <span className="rb-dot" style={{ background: LEVEL_COLORS[level] }}></span>
              Level {level}
            </div>
            {intro && <p className="rb-intro">{intro.text}</p>}
            {steps.length > 0 && (
              <ol className="rb-steps" start={steps[0].n}>
                {steps.map((s) => (
                  <li key={s.n} className="rb-step">
                    {tick ? tick(s) : <span className="rb-text">{s.text}</span>}
                  </li>
                ))}
              </ol>
            )}
          </section>
        )
      })}
    </div>
  )
}

type Draft = { level: number; text: string }

function RunbookModal({ appId, page, runbook, onClose }: { appId: number; page: AppRunbookPage; runbook: Runbook; onClose(): void }) {
  const query = useQueryClient()
  const levels = page.levels && page.levels.length > 0 ? page.levels : DEFAULT_LEVELS
  const [steps, setSteps] = useState<Draft[]>(
    runbook.steps.length > 0 ? runbook.steps.map((s) => ({ level: s.level, text: s.text })) : [{ level: 0, text: '' }],
  )
  const [busy, setBusy] = useState(false)

  const change = (i: number, patch: Partial<Draft>) => setSteps((all) => all.map((s, j) => (j === i ? { ...s, ...patch } : s)))
  const move = (i: number, by: number) =>
    setSteps((all) => {
      const j = i + by
      if (j < 0 || j >= all.length) return all
      const next = [...all]
      ;[next[i], next[j]] = [next[j], next[i]]
      return next
    })
  const remove = (i: number) => setSteps((all) => (all.length === 1 ? [{ level: 0, text: '' }] : all.filter((_, j) => j !== i)))

  const save = () => {
    const clean = steps.map((s) => ({ level: s.level, text: s.text.trim() })).filter((s) => s.text !== '')
    if (clean.length === 0) {
      toast('Write at least one step.', 'Runbook', 'error')
      return
    }
    setBusy(true)
    saveRunbook(appId, { steps: clean, base: runbook.base })
      .then((r) => {
        toast(r.message, 'Runbook', r.status === 'OK' ? 'success' : 'error')
        if (r.status === 'OK') {
          void query.invalidateQueries({ queryKey: ['business-apps'] })
          onClose()
        }
      })
      .catch((error) => toast(failed(error), 'Runbook', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <Modal
      id="runbookModal"
      open
      large
      onClose={onClose}
      title={`Edit runbook - ${runbook.title}`}
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Cancel
          </button>
          <button type="button" id="runbookSave" className="btn btn-primary" disabled={busy} onClick={save}>
            Save in IT Solutions
          </button>
        </>
      }
    >
      <p className="adm-muted small">
        One step per row, plain text, with the support level that does it. Steps are numbered in level order (Level 0 first); within a level, in
        the order written. They are saved as the numbered lists of the solution&apos;s levels - other text on those levels stays.
      </p>
      <ol className="rb-edit">
        {steps.map((s, i) => (
          <li key={i} className="rb-edit-row">
            <select className="form-control input-sm" aria-label={`Step ${i + 1} level`} value={s.level} onChange={(ev) => change(i, { level: Number(ev.target.value) })}>
              {levels.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>
            <textarea
              className="form-control input-sm"
              rows={2}
              maxLength={1000}
              aria-label={`Step ${i + 1}`}
              value={s.text}
              onChange={(ev) => change(i, { text: ev.target.value })}
            />
            <span className="rb-edit-actions">
              <button type="button" className="btn btn-default btn-xs" title="Move up" aria-label={`Move step ${i + 1} up`} disabled={i === 0} onClick={() => move(i, -1)}>
                <i className="fas fa-arrow-up"></i>
              </button>
              <button
                type="button"
                className="btn btn-default btn-xs"
                title="Move down"
                aria-label={`Move step ${i + 1} down`}
                disabled={i === steps.length - 1}
                onClick={() => move(i, 1)}
              >
                <i className="fas fa-arrow-down"></i>
              </button>
              <button type="button" className="btn btn-default btn-xs" title="Remove" aria-label={`Remove step ${i + 1}`} onClick={() => remove(i)}>
                <i className="fas fa-times"></i>
              </button>
            </span>
          </li>
        ))}
      </ol>
      <button
        type="button"
        id="runbookAddStep"
        className="btn btn-default btn-xs"
        disabled={steps.length >= 40}
        onClick={() => setSteps((all) => [...all, { level: all.length > 0 ? all[all.length - 1].level : 0, text: '' }])}
      >
        <i className="fas fa-plus"></i> Add step
      </button>
    </Modal>
  )
}

function LinkModal({ appId, onClose }: { appId: number; onClose(): void }) {
  const query = useQueryClient()
  const [search, setSearch] = useState('')
  const [found, setFound] = useState<RunbookSolution[] | null>(null)
  const [busy, setBusy] = useState(false)

  const find = () => {
    runbookSolutions(appId, search)
      .then((r) => setFound(r.solutions))
      .catch((error) => toast(failed(error), 'Runbook', 'error'))
  }

  const pick = (s: RunbookSolution) => {
    setBusy(true)
    linkRunbook(appId, s.id)
      .then((r) => {
        toast(r.message, 'Runbook', r.status === 'OK' ? 'success' : 'error')
        if (r.status === 'OK') {
          void query.invalidateQueries({ queryKey: ['business-apps'] })
          onClose()
        }
      })
      .catch((error) => toast(failed(error), 'Runbook', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <Modal
      id="runbookLinkModal"
      open
      onClose={onClose}
      title="Link an IT Solutions solution as the runbook"
      footer={
        <button type="button" className="btn btn-default" onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="rb-kb-search">
        <input
          type="text"
          className="form-control input-sm"
          id="runbookSolutionSearch"
          placeholder="Search the customer's IT Solutions"
          value={search}
          onChange={(ev) => setSearch(ev.target.value)}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') {
              ev.preventDefault()
              find()
            }
          }}
        />
        <button type="button" className="btn btn-default btn-sm" id="runbookSolutionFind" onClick={find}>
          Search
        </button>
      </div>
      {found !== null &&
        (found.length === 0 ? (
          <p className="adm-muted small">No solutions found.</p>
        ) : (
          <ul className="rb-kb-results">
            {found.map((s) => (
              <li key={s.id}>
                <button type="button" className="btn btn-link btn-xs" disabled={busy} onClick={() => pick(s)}>
                  {s.title}
                </button>
                <span className="adm-muted small"> · {s.category}</span>
              </li>
            ))}
          </ul>
        ))}
    </Modal>
  )
}
